import type { HTMLAttributes, ReactNode } from "react";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { uiMessages } from "@/i18n/messages/ui";
import { cn } from "@/lib/cn";
import { Button } from "./button";
import { Icons } from "./icons";

/**
 * The 32px header of a pocket panel (Settings, the search hub): the page
 * title, a back chevron on deeper pages, and a round close button. The whole
 * header is the drag handle; it shows no grip.
 */
export function PocketHeader({
  title,
  onBack,
  onClose,
  testIdPrefix,
  dragHandleProps,
}: {
  title: ReactNode;
  onBack?: () => void;
  onClose: () => void;
  testIdPrefix: string;
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
}) {
  const t = useT(uiMessages);
  const tc = useT(commonMessages);
  return (
    <div
      {...dragHandleProps}
      className={cn(
        "mb-1.5 flex h-8 shrink-0 cursor-grab items-center gap-1 pl-2 active:cursor-grabbing",
        dragHandleProps?.className,
      )}
      data-testid={`${testIdPrefix}-header`}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          onPointerDown={(event) => event.stopPropagation()}
          className="-ml-1.5 flex min-w-0 items-center gap-0.5 rounded-pill pr-2 text-left text-appkit-body font-semibold text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent"
          data-testid={`${testIdPrefix}-back`}
        >
          <Icons.ChevronLeft
            className="h-4 w-4 shrink-0 text-muted"
            aria-hidden="true"
          />
          <span className="sr-only">{t("backPrefix")} </span>
          <span className="truncate">{title}</span>
        </button>
      ) : (
        <h2 className="min-w-0 flex-1 truncate text-appkit-body font-semibold text-primary">
          {title}
        </h2>
      )}
      {onBack ? (
        <div
          className="min-w-8 flex-1"
          aria-hidden="true"
          data-testid={`${testIdPrefix}-drag-space`}
        />
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        onClick={onClose}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label={tc("close")}
        className="shrink-0 hover:bg-[var(--color-interactive-hover)] focus-visible:ring-2 focus-visible:ring-accent"
        data-testid={`${testIdPrefix}-close`}
      >
        <Icons.X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
